import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  PdfViewComponent,
  PdfViewerData
} from '@app/modules/super-admin/pdf-view/pdf-view.component';

@Injectable({ providedIn: 'root' })
export class DocumentViewerService {
  private externalHttp: HttpClient;

  constructor(private dialog: MatDialog, httpBackend: HttpBackend) {
    this.externalHttp = new HttpClient(httpBackend);
  }

  openDocumentReference(document: any): boolean {
    const key = document?.documentKey
      || document?.key
      || document?.imageKey
      || document?.signedUrl
      || document?.docUrl
      || document?.url;
    if (!key) {
      return false;
    }
    this.openKey(
      String(key),
      this.firstDocumentName(
        document?.document || document?.documentName || document?.name || document?.imageName
      )
    );
    return true;
  }

  openUrl(url: string, fileName?: string): MatDialogRef<PdfViewComponent> {
    return this.open({ url, fileName });
  }

  openKey(key: string, fileName?: string): MatDialogRef<PdfViewComponent> {
    return this.open({ key: this.normalizeDocumentKey(key), fileName });
  }

  openBlob(blob: Blob, fileName = 'document.pdf'): MatDialogRef<PdfViewComponent> {
    return this.open({ blob, fileName });
  }

  openBytes(
    bytes: Uint8Array | ArrayBuffer | ArrayBufferView | number[],
    fileName = 'document.pdf',
    mimeType = 'application/pdf'
  ): MatDialogRef<PdfViewComponent> {
    const binary = bytes instanceof ArrayBuffer
      ? new Uint8Array(bytes)
      : Array.isArray(bytes)
        ? new Uint8Array(bytes)
        : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const blob = new Blob([binary], { type: mimeType });
    return this.openBlob(blob, fileName);
  }

  openDocumentData(data: unknown, fileName = 'document.pdf'): boolean {
    if (data instanceof Blob) {
      this.openBlob(data, fileName);
      return true;
    }
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      this.openBytes(data, fileName);
      return true;
    }
    if (Array.isArray(data) && data.length > 0 && data.every(value => Number.isInteger(value) && value >= 0 && value <= 255)) {
      this.openBytes(data as number[], fileName);
      return true;
    }
    if (typeof data === 'string' && data.trim()) {
      const base64Data = this.parseBase64Document(data);
      if (base64Data) {
        this.openBlob(base64Data, fileName);
      } else if (/^https?:\/\//i.test(data)) {
        this.openUrl(data, fileName);
      } else {
        this.openKey(data, fileName);
      }
      return true;
    }
    if (data && typeof data === 'object') {
      const nodeBuffer = data as { type?: string; data?: unknown };
      if (nodeBuffer.type === 'Buffer' && nodeBuffer.data) {
        return this.openDocumentData(nodeBuffer.data, fileName);
      }
    }
    return false;
  }

  handleDocumentData(data: unknown, fileName = 'document.pdf', download = false): boolean {
    return download
      ? this.downloadDocumentData(data, fileName)
      : this.openDocumentData(data, fileName);
  }

  downloadDocumentData(data: unknown, fileName = 'document.pdf'): boolean {
    fileName = this.firstDocumentName(fileName);
    if (data instanceof Blob) {
      this.downloadBlob(data, fileName);
      return true;
    }
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      this.downloadBytes(data, fileName);
      return true;
    }
    if (Array.isArray(data) && data.length > 0 && data.every(value => Number.isInteger(value) && value >= 0 && value <= 255)) {
      this.downloadBytes(data as number[], fileName);
      return true;
    }
    if (typeof data === 'string' && data.trim()) {
      const base64Data = this.parseBase64Document(data);
      if (base64Data) {
        this.downloadBlob(base64Data, fileName);
        return true;
      }
      if (!/^https?:\/\//i.test(data)) {
        return false;
      }
      this.downloadRemoteDocument(data, fileName);
      return true;
    }
    if (data && typeof data === 'object') {
      const nodeBuffer = data as { type?: string; data?: unknown };
      if (nodeBuffer.type === 'Buffer' && nodeBuffer.data) {
        return this.downloadDocumentData(nodeBuffer.data, fileName);
      }
    }
    return false;
  }

  open(data: PdfViewerData): MatDialogRef<PdfViewComponent> {
    const normalizedData = {
      ...data,
      ...(data.fileName ? { fileName: this.firstDocumentName(data.fileName) } : {})
    };
    if (normalizedData.key) {
      normalizedData.key = this.normalizeDocumentKey(normalizedData.key);
    }
    return this.dialog.open(PdfViewComponent, {
      height: 'auto',
      width: '700px',
      minHeight: '500px',
      maxWidth: '95vw',
      autoFocus: false,
      data: normalizedData
    });
  }

  private downloadRemoteDocument(url: string, fileName: string): void {
    this.externalHttp.get(url, { responseType: 'blob' as const }).subscribe({
      next: (blob: Blob) => {
        if (blob) {
          this.downloadBlob(blob, fileName);
        } else {
          this.fallbackRemoteDownload(url, fileName);
        }
      },
      error: () => this.fallbackRemoteDownload(url, fileName)
    });
  }

  private fallbackRemoteDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private firstDocumentName(value: unknown): string {
    if (Array.isArray(value)) {
      return this.firstDocumentName(value[0]);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.length ? this.firstDocumentName(parsed[0]) : 'Document';
          }
        } catch (_error) {
          // Use the original value for legacy non-JSON names.
        }
      }
      return trimmed || 'Document';
    }
    return value ? String(value) : 'Document';
  }

  private downloadBytes(
    bytes: Uint8Array | ArrayBuffer | ArrayBufferView | number[],
    fileName: string
  ): void {
    const binary = bytes instanceof ArrayBuffer
      ? new Uint8Array(bytes)
      : Array.isArray(bytes)
        ? new Uint8Array(bytes)
        : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.downloadBlob(new Blob([binary], { type: 'application/pdf' }), fileName);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  private parseBase64Document(value: string): Blob | null {
    const match = value.match(/^data:([^;,]+)?(;base64)?,(.*)$/s);
    if (!match || !match[2]) {
      return null;
    }
    try {
      const binary = atob(match[3]);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
      }
      return new Blob([bytes], { type: match[1] || 'application/octet-stream' });
    } catch (_error) {
      return null;
    }
  }

  normalizeDocumentKey(value: string): string {
    let key = String(value || '').trim();
    if (!key) {
      return '';
    }

    if (/^https?:\/\//i.test(key)) {
      try {
        const url = new URL(key);
        const queryKey = url.searchParams.get('key');
        if (queryKey) {
          key = queryKey;
        }
      } catch (_error) {
        // Keep the original value when a malformed URL is supplied.
      }
    }

    const bracketStart = key.indexOf('[');
    if (bracketStart > -1 && key.endsWith(']')) {
      try {
        const values = JSON.parse(key.substring(bracketStart));
        if (Array.isArray(values) && values.length) {
          key = String(values[0]);
        }
      } catch (_error) {
        // Keep the original value for legacy non-JSON keys.
      }
    }

    try {
      key = decodeURIComponent(key);
    } catch (_error) {
      // The key is already usable when it contains malformed escape data.
    }

    return key.replace(/^\/+/, '').trim();
  }
}
